import { cn } from '@wellesley/ui'
import { Repeat } from 'lucide-react'
import CommonDialog from 'components/modals/CommonDialog'
import { ScrolledList } from 'components/modals/ScrolledList'
import { AppsLink } from '@wellesley/router'
import useActionGuard from 'hooks/useActionGuard'
import UsersListSkeleton from 'components/Skeletons/UsersListSkeleton'
import { useSwitchState } from 'hooks/useSwitchState'
import { getUserNameWithDomain } from 'helpers/getUserNameWithDomain'
import { FC, useRef, useCallback, useState, useEffect } from 'react'
import {
  Post,
  User,
  useGetApiV1PostsByPostIdRepostsQuery,
} from '@wellesley/openapi'

import { FeedProps } from '../TimelineFeed/types'
import { getDisplayUsername } from '@wellesley/user'

type RepostedLabelProps = Pick<FeedProps, 'renderUserWrapper'> & {
  feed: Post
}

const REPOSTERS_LIMIT = 2
const ICON_WIDTH = 16
const ICON_SPACE = 8
const NAME_SPACE = 5
const MINIMUM_NAME_WIDTH = 50

const RepostedLabel: FC<RepostedLabelProps> = ({ feed, renderUserWrapper }) => {
  const [isOpenModal, openModal, closeModal] = useSwitchState()

  const { guardedAction } = useActionGuard()

  // TODO delete after testing
  // const mockRepostedBy = [
  //   {
  //     id: '1',
  //     displayName:
  //       'Very Long Display Name That Should Get Truncated dsad asdasd dsрd',
  //     username: 'user1',
  //   },
  //   {
  //     id: '2',
  //     displayName: 'Another user tes dfdsf sdf sdfsdfsdf ',
  //     username: 'user2',
  //   },
  // ]

  const repostedBy = feed.repostedBy
  const repostsCounter = feed.stats.reposts || 0
  const hasReposters = !!feed.repostedBy?.length

  const getOtherUsersCounter = useCallback(
    () => repostsCounter - Math.min(repostedBy?.length || 0, REPOSTERS_LIMIT),
    [repostsCounter, repostedBy],
  )

  const [otherUsersCounter, setOtherUsersCounter] =
    useState(getOtherUsersCounter)

  useEffect(() => {
    setOtherUsersCounter(getOtherUsersCounter)
  }, [repostsCounter])

  const { data: reposters, isLoading: isLoadingReposters } =
    useGetApiV1PostsByPostIdRepostsQuery(
      {
        postId: feed.id,
        kind: 'REPOST',
      },
      { skip: !isOpenModal },
    )

  const isOnlyCurrentUserReposted = feed.stats.reposts === 1 && feed.reposted

  const ref = useRef<HTMLDivElement>(null)

  const [user1Width, setUser1Width] = useState(0)
  const [user2Width, setUser2Width] = useState(0)

  useEffect(() => {
    if (!ref.current?.clientWidth || !hasReposters) return

    const repostersContainerWidth = ref.current.children[1]
    const user1Width = repostersContainerWidth.children[0]?.clientWidth || 0
    const user2Width = repostersContainerWidth.children[1]?.clientWidth || 0

    const labelSuffixWidth = ref.current.children[2].clientWidth
    const availableWidth =
      ref.current.clientWidth - ICON_WIDTH - ICON_SPACE - labelSuffixWidth

    const isUser1LongerThanAvailableWidth = user1Width > availableWidth
    const isNotEnoughSpaceForUser2 =
      availableWidth - user1Width < MINIMUM_NAME_WIDTH

    if (isUser1LongerThanAvailableWidth || isNotEnoughSpaceForUser2) {
      setUser1Width(availableWidth)
      setUser2Width(0)
      if (repostedBy && repostedBy.length > 1) {
        setOtherUsersCounter(repostsCounter - 1)
      }
    } else if (availableWidth < user1Width + user2Width) {
      setUser1Width(user1Width + NAME_SPACE)
      setUser2Width(availableWidth - user1Width)
      if (repostedBy && repostedBy.length >= REPOSTERS_LIMIT) {
        setOtherUsersCounter(repostsCounter - REPOSTERS_LIMIT)
      }
    }
  }, [ref.current?.clientWidth, hasReposters])

  const getMaxWidth = (index: number) =>
    user1Width > 0 ? (index === 0 ? user1Width : user2Width) : 'unset'

  if (isOnlyCurrentUserReposted) return null

  return (
    <div
      ref={ref}
      data-testid="reposted-label"
      className="mb-xxs flex items-center whitespace-nowrap text-sm font-semibold text-gray-500"
    >
      <Repeat size={16} stroke="var(--gray-500)" className="mr-xxs" />
      <div className="flex min-w-0 max-w-full truncate empty:hidden">
        {repostedBy?.slice(0, REPOSTERS_LIMIT).map((user, index) => {
          const width = getMaxWidth(index)

          if (width === 0) return null

          return (
            <div
              key={user.id}
              className={cn('inline-block', !!user1Width && 'truncate')}
              style={{ width }}
            >
              <AppsLink
                to="/@/:username"
                props={{
                  username: getUserNameWithDomain(user as unknown as User),
                }}
              >
                {index > 0 && ', '}
                {renderUserWrapper?.({
                  user: user as unknown as User,
                  el: (
                    <span className="cursor-pointer font-semibold hover:text-blue-500">
                      {user.displayName}
                    </span>
                  ),
                })}
              </AppsLink>
            </div>
          )
        })}
      </div>

      <span>
        {otherUsersCounter > 0 ? (
          <>
            {hasReposters && <>&nbsp;and&nbsp;</>}
            <span
              onClick={guardedAction(openModal)}
              className="cursor-pointer font-normal hover:underline"
            >
              {otherUsersCounter} other reposted
            </span>
          </>
        ) : (
          <>&nbsp;reposted</>
        )}
      </span>

      <CommonDialog
        open={isOpenModal}
        title={`Reposted ${getDisplayUsername(feed.author.username)}`}
        onCloseModal={closeModal}
        className="min-h-[500px] content-start"
      >
        {isLoadingReposters ? (
          <UsersListSkeleton count={8} />
        ) : (
          <ScrolledList
            onClose={closeModal}
            list={reposters?.map(({ user }) => user)}
          />
        )}
      </CommonDialog>
    </div>
  )
}

export default RepostedLabel
